import { useState, useRef, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import type { Task, Sprint } from '../page';
import TaskComments from './TaskComments';
import TaskViewModal from './TaskViewModal';
import { triggerChangeStepWebhook } from '@/hooks/useWebhook';

interface ClientBacklogProps {
  projectId: string;
  tasks: Task[];
  sprints: Sprint[];
  userId: string;
  userProfile: { id: string; full_name?: string; role?: string } | null;
  project: { id: string; name: string; status: string; client_id: string | null; _client_name: string } | null;
  onRefresh: () => void;
  openTaskId?: string | null;
  onTaskOpened?: () => void;
}

const PRIORITY_CONFIG: Record<string, { label: string; color: string; icon: string; order: number }> = {
  critical: { label: 'Crítica', color: 'text-red-700', icon: 'ri-arrow-up-double-line', order: 0 },
  high: { label: 'Alta', color: 'text-red-500', icon: 'ri-arrow-up-line', order: 1 },
  medium: { label: 'Média', color: 'text-orange-400', icon: 'ri-arrow-right-line', order: 2 },
  low: { label: 'Baixa', color: 'text-slate-400', icon: 'ri-arrow-down-line', order: 3 },
};

interface TaskForm {
  title: string;
  description: string;
  type: 'feature' | 'bug';
  priority: string;
  observations: string;
}

const emptyForm: TaskForm = { title: '', description: '', type: 'feature', priority: 'medium', observations: '' };

export default function ClientBacklog({ projectId, tasks, sprints, userId, userProfile, project, onRefresh, openTaskId, onTaskOpened }: ClientBacklogProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [viewTask, setViewTask] = useState<Task | null>(null);
  const [form, setForm] = useState<TaskForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [commentsTask, setCommentsTask] = useState<Task | null>(null);
  const [sortBy, setSortBy] = useState<'priority' | 'created_at'>('priority');
  const [filterType, setFilterType] = useState('');
  const [movingId, setMovingId] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Image attachment state
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const backlogTasks = tasks.filter(t => t.board_status === 'backlog');
  const filtered = backlogTasks.filter(t => !filterType || t.type === filterType);
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'priority') {
      return (PRIORITY_CONFIG[a.priority]?.order ?? 99) - (PRIORITY_CONFIG[b.priority]?.order ?? 99);
    }
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const openNew = () => {
    setEditingTask(null);
    setForm(emptyForm);
    setImageFile(null);
    setImagePreview(null);
    setExistingImageUrl(null);
    setModalOpen(true);
  };

  const openEdit = (task: Task) => {
    setEditingTask(task);
    setForm({
      title: task.title,
      description: task.description || '',
      type: task.type,
      priority: task.priority,
      observations: task.observations || '',
    });
    setImageFile(null);
    // Load existing image from attachments
    const attachments = (task as Task & { attachments?: { images?: string[] } }).attachments;
    const existingImg = attachments?.images?.[0] ?? null;
    setExistingImageUrl(existingImg);
    setImagePreview(existingImg);
    setModalOpen(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Imagem muito grande. Máximo 5MB.');
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setExistingImageUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadImage = async (taskId: string): Promise<string | null> => {
    if (!imageFile) return existingImageUrl;
    setUploadProgress(true);
    const ext = imageFile.name.split('.').pop();
    const path = `${taskId}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage
      .from('task-attachments')
      .upload(path, imageFile, { upsert: true });
    setUploadProgress(false);
    if (error) {
      console.error('Erro ao fazer upload:', error);
      return null;
    }
    const { data } = supabase.storage.from('task-attachments').getPublicUrl(path);
    return data.publicUrl;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);

    if (editingTask) {
      let imageUrl = existingImageUrl;
      if (imageFile) {
        imageUrl = await uploadImage(editingTask.id);
      }
      const attachments = imageUrl ? { images: [imageUrl] } : null;

      const { error } = await supabase.from('tasks').update({
        title: form.title,
        description: form.description,
        type: form.type,
        priority: form.priority,
        observations: form.observations,
        attachments,
        updated_at: new Date().toISOString(),
      }).eq('id', editingTask.id);
      if (error) console.error('Erro ao editar:', error);
    } else {
      const maxPos = backlogTasks.length > 0 ? Math.max(...backlogTasks.map(t => t.position)) + 1 : 0;
      const { data: newTask, error } = await supabase.from('tasks').insert({
        project_id: projectId,
        title: form.title,
        description: form.description,
        type: form.type,
        priority: form.priority,
        observations: form.observations,
        board_status: 'backlog',
        status: 'backlog',
        position: maxPos,
        created_by: userId,
        value: 0,
        paid: false,
        sprint_desired: false,
        estimate_hours: 0,
        estimate_days: 0,
        attachments: null,
      }).select().maybeSingle();

      if (error) {
        console.error('Erro ao criar:', error);
        setSaving(false);
        return;
      }

      // Upload image after task is created (we need the task ID)
      if (imageFile && newTask) {
        const imageUrl = await uploadImage(newTask.id);
        if (imageUrl) {
          await supabase.from('tasks').update({
            attachments: { images: [imageUrl] },
          }).eq('id', newTask.id);
        }
      }
    }

    setSaving(false);
    setModalOpen(false);
    setSuccessMsg(editingTask ? 'Solicitação atualizada!' : 'Solicitação criada com sucesso!');
    setTimeout(() => setSuccessMsg(''), 3000);
    onRefresh();
  };

  const handleDelete = async (taskId: string) => {
    await supabase.from('tasks').delete().eq('id', taskId);
    setDeleteConfirm(null);
    onRefresh();
  };

  const toggleSprintDesired = async (task: Task) => {
    await supabase.from('tasks').update({
      sprint_desired: !task.sprint_desired,
      updated_at: new Date().toISOString(),
    }).eq('id', task.id);
    onRefresh();
  };

  const moveToTodo = async (task: Task) => {
    setMovingId(task.id);
    const todoTasks = tasks.filter(t => t.board_status === 'todo');
    const maxPriority = todoTasks.length > 0
      ? Math.max(...todoTasks.map(t => (t as Task & { client_priority?: number }).client_priority ?? 0)) + 1
      : 1;

    const { error } = await supabase.from('tasks').update({
      board_status: 'todo',
      status: 'todo',
      client_priority: maxPriority,
      updated_at: new Date().toISOString(),
    }).eq('id', task.id);

    if (!error) {
      // Buscar dados do cliente para o webhook
      let clientEmail: string | null = null;
      if (project?.client_id) {
        const { data: clientData } = await supabase
          .from('clients')
          .select('email')
          .eq('id', project.client_id)
          .maybeSingle();
        clientEmail = clientData?.email ?? null;
      }

      await triggerChangeStepWebhook({
        event: 'change_step',
        action: 'moved_to_todo',
        task: {
          id: task.id,
          title: task.title,
          description: task.description,
          type: task.type,
          priority: task.priority,
          board_status: 'todo',
          client_priority: maxPriority,
          estimate_hours: task.estimate_hours,
          estimate_days: task.estimate_days,
          value: task.value,
          paid: task.paid,
          sprint_desired: task.sprint_desired,
          sprint_id: task.sprint_id,
          observations: task.observations,
          created_at: task.created_at,
        },
        project: {
          id: project?.id ?? projectId,
          name: project?.name ?? '',
          status: project?.status ?? '',
          client_id: project?.client_id ?? null,
        },
        client: {
          id: project?.client_id ?? null,
          name: project?._client_name ?? null,
          email: clientEmail,
        },
        triggered_by: {
          user_id: userId,
          user_name: userProfile?.full_name ?? null,
          role: userProfile?.role ?? null,
        },
        timestamp: new Date().toISOString(),
      });
    }

    setMovingId(null);
    setSuccessMsg(`"${task.title}" movida para A Fazer!`);
    setTimeout(() => setSuccessMsg(''), 3000);
    onRefresh();
  };

  const canEdit = (task: Task) => task.created_by === userId || userProfile?.role === 'admin';
  const canDelete = (task: Task) => task.created_by === userId && task.board_status === 'backlog';

  const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const fmtDate = (d: string) => new Date(d).toLocaleDateString('pt-BR');
  const sprintName = (id: string | null) => sprints.find(s => s.id === id)?.name || null;

  // Auto-open TaskViewModal when coming from a notification
  useEffect(() => {
    if (!openTaskId || tasks.length === 0) return;
    const target = tasks.find(t => t.id === openTaskId);
    if (target) {
      setViewTask(target);
      onTaskOpened?.();
    }
  }, [openTaskId, tasks]);

  return (
    <div>
      {/* Toast de sucesso */}
      {successMsg && (
        <div className="fixed top-5 right-5 z-50 bg-emerald-500 text-white px-5 py-3 rounded-xl text-sm font-medium flex items-center gap-2 animate-fade-in">
          <i className="ri-checkbox-circle-line text-base"></i>
          {successMsg}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer"
        >
          <option value="">Todos os tipos</option>
          <option value="feature">Nova Demanda</option>
          <option value="bug">Bug/Correção</option>
        </select>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value as 'priority' | 'created_at')}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer"
        >
          <option value="priority">Ordenar por prioridade</option>
          <option value="created_at">Ordenar por data</option>
        </select>
        <div className="ml-auto">
          <button
            onClick={openNew}
            className="flex items-center gap-1.5 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Solicitar Item
          </button>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-slate-400">
          <i className="ri-list-check-2 text-4xl mb-3"></i>
          <p className="text-sm font-medium text-slate-500">Backlog vazio</p>
          <p className="text-xs mt-1">Solicite novas demandas ou correções</p>
          <button onClick={openNew} className="mt-4 text-cyan-500 text-sm hover:underline cursor-pointer">
            Criar primeira solicitação
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-3 text-slate-500 font-medium w-8"></th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Item</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Tipo</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Prioridade</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Estimativa</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Valor</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Sprint</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Data</th>
                  <th className="text-right px-4 py-3 text-slate-500 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(task => {
                  const prio = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
                  const taskAttachments = (task as Task & { attachments?: { images?: string[] } }).attachments;
                  const hasImage = taskAttachments?.images && taskAttachments.images.length > 0;
                  return (
                    <tr key={task.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setViewTask(task)}
                          title="Visualizar item"
                          className="w-6 h-6 flex items-center justify-center cursor-pointer text-slate-300 hover:text-cyan-500 transition-colors"
                        >
                          <i className="ri-eye-line text-sm"></i>
                        </button>
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          {hasImage && (
                            <img
                              src={taskAttachments!.images![0]}
                              alt="anexo"
                              className="w-8 h-8 rounded object-cover flex-shrink-0 border border-slate-200"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-slate-800 font-medium truncate overflow-hidden whitespace-nowrap">{task.title}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          task.type === 'bug' ? 'bg-red-50 text-red-600' : 'bg-violet-50 text-violet-600'
                        }`}>
                          <i className={task.type === 'bug' ? 'ri-bug-line' : 'ri-add-circle-line'}></i>
                          {task.type === 'bug' ? 'Bug' : 'Demanda'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium ${prio.color}`}>
                          <i className={prio.icon}></i>
                          {prio.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">
                        {task.estimate_hours > 0 ? `${task.estimate_hours}h` : ''}
                        {task.estimate_hours > 0 && task.estimate_days > 0 ? ' / ' : ''}
                        {task.estimate_days > 0 ? `${task.estimate_days}d` : ''}
                        {!task.estimate_hours && !task.estimate_days ? '—' : ''}
                      </td>
                      <td className="px-4 py-3">
                        {task.value > 0 ? (
                          <div className="flex items-center gap-1">
                            <span className="text-slate-700 text-xs font-medium">{fmt(task.value)}</span>
                            {task.paid && <i className="ri-checkbox-circle-fill text-emerald-500 text-xs" title="Pago"></i>}
                          </div>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {task.sprint_id ? (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-cyan-50 text-cyan-700">{sprintName(task.sprint_id)}</span>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-xs">{fmtDate(task.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setCommentsTask(task)}
                            title="Comentários"
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-chat-3-line text-sm"></i>
                          </button>
                          {canEdit(task) && (
                            <button
                              onClick={() => openEdit(task)}
                              title="Editar"
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
                            >
                              <i className="ri-edit-line text-sm"></i>
                            </button>
                          )}
                          {canDelete(task) && (
                            <button
                              onClick={() => setDeleteConfirm(task.id)}
                              title="Excluir"
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal criar/editar */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
              <h2 className="text-slate-800 font-semibold text-base">
                {editingTask ? 'Editar Solicitação' : 'Nova Solicitação'}
              </h2>
              <button onClick={() => setModalOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <form onSubmit={handleSave} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Título *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Descreva a solicitação..."
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Tipo</label>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setForm(f => ({ ...f, type: 'feature' }))}
                      className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-xs font-medium border transition-colors cursor-pointer whitespace-nowrap ${form.type === 'feature' ? 'bg-violet-50 border-violet-300 text-violet-700' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                      <i className="ri-add-circle-line"></i>Demanda
                    </button>
                    <button type="button" onClick={() => setForm(f => ({ ...f, type: 'bug' }))}
                      className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-xs font-medium border transition-colors cursor-pointer whitespace-nowrap ${form.type === 'bug' ? 'bg-red-50 border-red-300 text-red-700' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                      <i className="ri-bug-line"></i>Bug
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Prioridade</label>
                  <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer">
                    <option value="low">Baixa</option>
                    <option value="medium">Média</option>
                    <option value="high">Alta</option>
                    <option value="critical">Crítica</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Descrição</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={3} maxLength={500} placeholder="Detalhes da solicitação..."
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300 resize-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Observações</label>
                <textarea value={form.observations} onChange={e => setForm(f => ({ ...f, observations: e.target.value }))}
                  rows={2} maxLength={500} placeholder="Informações adicionais..."
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300 resize-none" />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Imagem anexada <span className="text-slate-400 font-normal">(opcional, máx. 5MB)</span>
                </label>
                {imagePreview ? (
                  <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-40 object-contain"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-white/90 hover:bg-red-50 text-slate-500 hover:text-red-500 rounded-full border border-slate-200 transition-colors cursor-pointer"
                      title="Remover imagem"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                    {imageFile && (
                      <div className="absolute bottom-2 left-2 bg-white/90 px-2 py-0.5 rounded text-xs text-slate-500 border border-slate-200">
                        {imageFile.name}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex flex-col items-center justify-center gap-2 py-6 border-2 border-dashed border-slate-200 rounded-lg hover:border-cyan-300 hover:bg-cyan-50/30 transition-colors cursor-pointer group"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 group-hover:bg-cyan-100 transition-colors">
                      <i className="ri-image-add-line text-slate-400 group-hover:text-cyan-500 text-lg"></i>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-slate-500 group-hover:text-cyan-600 font-medium">Clique para anexar imagem</p>
                      <p className="text-xs text-slate-400 mt-0.5">PNG, JPG, GIF, WebP — até 5MB</p>
                    </div>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={() => setModalOpen(false)}
                  className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                  Cancelar
                </button>
                <button type="submit" disabled={saving || uploadProgress}
                  className="px-5 py-2 bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-300 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                  {(saving || uploadProgress) && <i className="ri-loader-4-line animate-spin text-sm"></i>}
                  {saving || uploadProgress ? 'Salvando...' : editingTask ? 'Salvar' : 'Criar Solicitação'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm">
            <h3 className="text-slate-800 font-semibold mb-2">Excluir solicitação?</h3>
            <p className="text-slate-500 text-sm mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap">Excluir</button>
            </div>
          </div>
        </div>
      )}

      {/* Comentários */}
      {commentsTask && (
        <TaskComments
          task={commentsTask}
          userId={userId}
          userName={userProfile?.full_name || 'Usuário'}
          onClose={() => setCommentsTask(null)}
        />
      )}

      {/* Modal visualizar item com chat integrado */}
      {viewTask && (
        <TaskViewModal
          task={viewTask}
          userId={userId}
          userName={userProfile?.full_name || 'Usuário'}
          onClose={() => setViewTask(null)}
          canEdit={canEdit(viewTask)}
          onEdit={() => { setViewTask(null); openEdit(viewTask); }}
        />
      )}
    </div>
  );
}
